'use client';

import { memo, useState } from 'react';
import { Card } from '@/components/common/Card';
import { AppImage } from '@/components/common/AppImage';
import { formatCurrency } from '@/lib/utils/format';
import type { Team } from '@/lib/types/auction';
import { ChevronDown, ChevronUp, Users } from 'lucide-react';
import { useRouter } from 'next/navigation';

interface TeamPurseOverviewProps {
  teams: Team[];
}

export const TeamPurseOverview = memo(({ teams }: TeamPurseOverviewProps) => {
  const [sortDescending, setSortDescending] = useState(true);
  const router = useRouter();

  // ✅ Sort teams by remaining budget
  const sortedTeams = [...teams].sort((a, b) =>
    sortDescending
      ? b.budget - b.spent - (a.budget - a.spent)
      : a.budget - a.spent - (b.budget - b.spent)
  );

  const handleTeamClick = (teamId: string) => {
    router.push(`/team/${teamId}`);
  };

  return (
    <Card className="flex h-full flex-col p-2">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">
          Team Purse Overview
        </h3>

        {/* 🔹 Sorting Toggle */}
        <button
          className="flex items-center space-x-1 text-sm font-medium text-gray-700 hover:text-gray-900"
          onClick={() => setSortDescending(!sortDescending)}
        >
          <span>Sort by Budget Left</span>
          {sortDescending ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronUp className="h-4 w-4" />
          )}
        </button>
      </div>

      {/* ✅ Empty State */}
      {teams.length === 0 ? (
        <p className="text-center text-gray-500">No teams available</p>
      ) : (
        <div className="flex-1 space-y-4 overflow-auto">
          {sortedTeams.map((team) => {
            const remainingBudget = team.budget - team.spent;
            const percentageSpent = (team.spent / team.budget) * 100;
            const boughtPlayersCount = team.playerCount || 0;

            // 🔥 Dynamic Color for Progress Bar
            const getBarColor = () => {
              if (percentageSpent <= 50) return 'bg-green-500'; // Safe Zone
              if (percentageSpent <= 80) return 'bg-yellow-500'; // Warning Zone
              return 'bg-red-500'; // Critical Zone
            };

            return (
              <div
                key={team.id}
                className="cursor-pointer rounded-lg border border-gray-200 bg-gray-50 p-4 shadow-sm transition-all duration-200 hover:bg-gray-100 hover:shadow-md"
                onClick={() => handleTeamClick(team.id)}
              >
                {/* 🔹 Team Info */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    {/* ✅ Fixed Image Issue */}
                    <div className="h-10 w-10 overflow-hidden rounded-full border border-gray-300 bg-gray-100 shadow-sm">
                      <AppImage
                        src={team.logo || ''}
                        alt={team.name}
                        shape="circle"
                        size="sm"
                        gradient
                        className="h-full w-full object-cover"
                        fallbackText={team.name?.[0] || 'T'}
                      />
                    </div>
                    <div className="flex flex-col">
                      <span className="text-sm font-medium text-gray-700">
                        {team.name}
                      </span>
                      {/* 🔹 Bought Players Count */}
                      <div className="flex items-center gap-1 text-xs text-gray-500">
                        <Users className="h-3 w-3" />
                        <span>{boughtPlayersCount} players bought</span>
                      </div>
                    </div>
                  </div>

                  {/* 🔹 Budget Label (Improved Visibility) */}
                  <span
                    className={`rounded px-2 py-1 text-sm font-semibold ${
                      remainingBudget > 0
                        ? 'bg-blue-100 text-blue-600'
                        : 'bg-red-100 text-red-600'
                    }`}
                  >
                    {formatCurrency(remainingBudget)} left
                  </span>
                </div>

                {/* 🔹 Budget Progress Bar */}
                <div className="relative mt-2 h-3 w-full rounded-full bg-gray-200">
                  <div
                    className={`absolute left-0 top-0 h-full rounded-full transition-all ${getBarColor()}`}
                    style={{ width: `${Math.max(0, percentageSpent)}%` }}
                  />
                </div>

                {/* 🔹 Spent vs Total */}
                <div className="mt-1 flex justify-between text-xs text-gray-500">
                  <span>Spent: {formatCurrency(team.spent)}</span>
                  <span>Total: {formatCurrency(team.budget)}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
});
